import { createLocalizedClient } from '@/http/client';
import type {
    AvailableFilter,
    Bookmark,
    DashboardStatistics,
    PaginatedResponse,
    Person,
    PersonFilters,
    RelatedPersonFilters,
    RelatedSortOption,
    ScrapeSourceOption,
    ScrapedPerson,
    SortValue
} from '@/types';

const client = createLocalizedClient({
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api/v1`
});

export default abstract class Api {
    public static client = client;

    public static getCurrentUser() {
        return Api.client.get('/me');
    }

    public static getPersons(
        page: number | string,
        itemsPerPage: number,
        query: string | undefined,
        filters: PersonFilters,
        sortBy: SortValue = {}
    ): Promise<PaginatedResponse<Person>> {
        return Api.client.get('persons', {
            params: {
                page,
                perPage: itemsPerPage,
                orderBy: sortBy?.field,
                orderDir: sortBy?.order,
                query: query,
                filters: filters
            }
        }).then((response) => response.data);
    }

    public static getPerson(id: string | number): Promise<Person> {
        return Api.client.get(`persons/${id}`).then(function (response) {
            return response.data;
        });
    }

    public static createPerson(data: Partial<Person>): Promise<Person> {
        return Api.client.post('persons', data).then((response) => response.data);
    }

    public static updatePerson(id: string | number, data: Partial<Person>): Promise<Person> {
        return Api.client.put(`persons/${id}`, data).then((response) => response.data);
    }

    public static scrapePerson(source: string, name: string): Promise<ScrapedPerson> {
        return Api.client.get(`persons/scrape/${source}/${name}`).then((response) => response.data);
    }

    public static getScrapeSources(): Promise<ScrapeSourceOption[]> {
        return Api.client.get('persons/scrape-sources').then((response) => response.data);
    }

    public static getRelatedPersons(
        id: string | number,
        filters: RelatedPersonFilters,
        page: number,
        itemsPerPage: number,
        sortBy: RelatedSortOption = []
    ): Promise<PaginatedResponse<Person>> {
        return Api.client.get(`persons/${id}/related`, {
            params: {
                page,
                perPage: itemsPerPage,
                orderBy: sortBy[0]?.key,
                orderDir: sortBy[0]?.order,
                cup_size: filters.cup_size,
                sister_cup_size: filters.sister_cup_size
            }
        }).then((response) => response.data);
    }

    public static getAvailableFilters(): Promise<AvailableFilter[]> {
        return Api.client.get('persons/available-filters')
            .then(function (response) {
                return response.data;
            }
        );
    }

    public static getMissingValueFilters(): Promise<AvailableFilter[]> {
        return Api.client.get('persons/missing-values/available-filters')
            .then(function (response) {
                return response.data;
            }
        );
    }

    public static getBookmarks(id: string | number): Promise<Bookmark[]> {
        return Api.client.get(`persons/${id}/bookmarks`).then((response) => response.data);
    }

    public static getGallery(id: string | number): Promise<string[]> {
        return Api.client.get(`persons/${id}/gallery`).then((response) => response.data);
    }

    public static getStatistics(): Promise<DashboardStatistics> {
        return Api.client.get('persons/statistics').then((response) => response.data);
    }

    public static getMissingValuePersons(
        page: number | string,
        itemsPerPage: number,
        query: string | undefined,
        filters: PersonFilters,
        sortBy: SortValue = {}
    ): Promise<PaginatedResponse<Person>> {
        return Api.client.get('persons/missing-values', {
            params: {
                page,
                perPage: itemsPerPage,
                orderBy: sortBy?.field,
                orderDir: sortBy?.order,
                query: query,
                filters: filters
            }
        }).then((response) => response.data);
    }

    public static getRandomPerson(): Promise<Person> {
        return Api.client.get('persons/random').then((response) => response.data);
    }
}
